<template>
  <div class="modal fade" :id="id" data-backdrop="static" tabindex="-1" role="dialog" aria-labelledby="staticBackdrop" aria-hidden="true">
    <div class="modal-dialog modal-md" role="document">
      <form @submit.prevent="submitTransferForm" class="form">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ $t('financial.accounts.box_transfer') }}</h5>
            <div class="btn btn-icon btn-sm btn-active-light-primary ms-2" @click="closeModal">
                <span class="svg-icon svg-icon-1">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <rect opacity="0.5" x="6" y="17.3137" width="16" height="2" rx="1" transform="rotate(-45 6 17.3137)"
                          fill="currentColor" />
                    <rect x="7.41422" y="6" width="16" height="2" rx="1" transform="rotate(45 7.41422 6)"
                          fill="currentColor" />
                  </svg>
                </span>
            </div>
          </div>

          <div class="modal-body">
            <div class="form-group mb-5">
              <div class="form-label d-flex justify-content-center align-items-center w-100 text-center">
                <div class="radio-inline d-flex gap-4">
                  <label class="radio radio-primary d-flex align-items-center">
                    <input type="radio" v-model="formData.type" value="box" name="transfer_type" required/>
                    <span class="ms-3">{{$t('financial.accounts.box_title')}}</span>
                  </label>
                  <label class="radio radio-primary d-flex align-items-center">
                    <input type="radio" v-model="formData.type" value="account" name="transfer_type" required/>
                    <span class="ms-3">{{$t('financial.accounts.account_title')}}</span>
                  </label>
                </div>
              </div>
            </div>

            <div v-if="formData.type === 'account'" class="form-group">
              <div class="input-group">
                <Select2
                    :parent-id="`#${id}`"
                    containerClass="col-12 mt-auto mb-5"
                    :name="`${id}-sub-accounts`"
                    :multiple="false"
                    :label="$t('financial.accounts.credit_accounts')"
                    labelClass="d-flex align-items-center fs-6 fw-bold mb-2"
                    selectClass="form-select form-select-solid"
                    :isRequired="false"
                    :placeholderText="$t('financial.accounts.credit_accounts_placeholder')"
                    v-model="formData.credit_box_id"
                >
                  <option :value="account.id" v-for="account in getAccounts" :key="account.id">{{ account.name }}</option>
                </Select2>
              </div>
            </div>

            <div v-if="formData.type === 'box'" class="form-group">
              <div class="input-group">
                <Select2
                    :parent-id="`#${id}`"
                    containerClass="col-12 mt-auto mb-5"
                    :name="`${id}-sub-boxes`"
                    :multiple="false"
                    :label="$t('financial.accounts.credit_boxes')"
                    labelClass="d-flex align-items-center fs-6 fw-bold mb-2"
                    selectClass="form-select form-select-solid"
                    :isRequired="false"
                    :placeholderText="$t('financial.accounts.credit_boxes_placeholder')"
                    v-model="formData.credit_box_id"
                >
                  <option :value="box.id" v-for="box in getBoxesToCredit" :key="box.id">{{ box.name }}</option>
                </Select2>
              </div>
            </div>

            <Input
              v-show="selectedBoxCurrency !== deviseExchange"
              :name="'exchange'+id"
              type="text"
              :label="$t('invoice.label.exchange')"
              labelClass="form-label fs-6 text-black"
              :inputClass="'form-control form-control-sm form-control-solid'"
              :isRequired="true"
              size="lg"
              :placeholder="$t('invoice.placeholder.exchange')"
              v-model="formData.rate"
              :IsInputGroup="true"
              addon-class="border-0 border-end"
              iconDir="left"
              :iconText="'1 '+ deviseExchange +' ='">
              <template #rightAddon>
                <span class="position-absolute end-0 top-50 translate-middle-y me-3 text-muted fw-bolder">
                  {{ selectedBoxCurrency }}
                </span>
              </template>
            </Input>

            <div class="form-group mb-5">
              <label class="form-label fs-6 text-black">{{ $t('financial.accounts.amount') }}</label>

              <VueNumberFormat v-model:value="formData.amount" class="form-control form-control-solid" :options="{ precision: 0, prefix: '', acceptNegative: false, isInteger: false  }"
              ></VueNumberFormat>
            </div>

            <div class="form-group">
              <label class="form-label fs-6 text-black">{{ $t('financial.accounts.reason') }}</label>
              <textarea v-model="formData.reason" class="form-control form-control-solid" rows="3" required></textarea>
            </div>
          </div>

          <div class="modal-footer">
            <Button type="cancel" @click="closeModal" :id="id+'_cancel_btn'" btn-class="btn-sm me-3" size="7px 30px" btn-text="button.cancel"></Button>
            <Button btn-class="btn-sm me-3" type="submit" :id="id+'_submit_btn'" btn-text="button.save" size="7px 30px" :is-loading="isLoadingTransfer"></Button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>
<script setup>

import { ref, computed, defineEmits } from 'vue';
import InputAmount from "@/components/InputAmount";
import Select2 from "@/components/Select2";
import {useStore} from "vuex";
import Button from "@/components/Button.vue"
import VueNumberFormat from 'vue-number-format'
import Input from "@/components/Input"

const props = defineProps({
  id: String,
  getBoxesToCredit: Array,
  getAccounts: Array,
  deviseExchange: String,
  form: {
    type: Object,
    default: {
      box_id: '',
      debit_box_id: '',
      type: 'box',
      credit_box_id: '',
      amount: 0,
      rate: 1,
      reason: ''
    }
  }
});

const emits = defineEmits(['load']);
const store = useStore()

const formData = ref(props.form);
const isLoadingTransfer = ref(false)

const boxes = computed(() => store.getters['accounts/getBoxes'])
const accounts = computed(() => store.getters['accounts/getAccounts'])

const selectedBoxCurrency = computed(() => {
  if(formData.value.credit_box_id){
    let box = Object.values(boxes.value || {}).find(a => a.id == formData.value.credit_box_id)
    let account = Object.values(accounts.value || {}).find(a => a.id == formData.value.credit_box_id)
    return box ? box.currency_iso : account.currency_iso
  }
})

const closeModal = () => {
  $('#'+props.id).modal('toggle')
};

const submitTransferForm = () => {
  isLoadingTransfer.value = true
  store.dispatch('accounts/createBoxTransfer', formData.value)
      .then((response) => {
          emits('load')
          closeModal()
          isLoadingTransfer.value = false
          formData.value.amount = 0,
          formData.value.reason = '',
          formData.value.type = 'box',
          formData.value.credit_box_id = '',
          formData.value.rate = 1

          window.toastr['success'](i18n.global.t('notification.transfer_created_successfully'))
      })
      .catch(err => {
          isLoadingTransfer.value = false
      })
};
</script>